<script>
export default {
  name: 'SendListModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'send'],
  data() {
    return {
      body: '',
      buttonText: '',
      footer: '',
      sections: [
        {
          title: '',
          rows: [{ id: '1', title: '', description: '' }],
        },
      ],
      isSending: false,
    };
  },
  computed: {
    validSections() {
      return this.sections
        .map(s => ({
          ...s,
          validRows: s.rows.filter(r => r.title.trim().length > 0),
        }))
        .filter(s => s.validRows.length > 0);
    },
    isValid() {
      return (
        this.body.trim().length > 0 &&
        this.buttonText.trim().length > 0 &&
        this.validSections.length > 0
      );
    },
  },
  methods: {
    onClose() {
      this.resetForm();
      this.$emit('close');
    },
    addSection() {
      if (this.sections.length < 10) {
        this.sections.push({
          title: '',
          rows: [{ id: '1', title: '', description: '' }],
        });
      }
    },
    removeSection(sIdx) {
      this.sections.splice(sIdx, 1);
    },
    addRow(sIdx) {
      const section = this.sections[sIdx];
      if (section.rows.length < 10) {
        section.rows.push({
          id: String(section.rows.length + 1),
          title: '',
          description: '',
        });
      }
    },
    removeRow(sIdx, rIdx) {
      this.sections[sIdx].rows.splice(rIdx, 1);
      this.sections[sIdx].rows.forEach((r, i) => {
        r.id = String(i + 1);
      });
    },
    send() {
      if (!this.isValid) return;
      this.isSending = true;

      const sectionsPayload = this.validSections.map(s => ({
        Title: s.title || '',
        Rows: s.validRows.map((r, i) => ({
          RowId: String(i + 1),
          Title: r.title.trim(),
          Description: r.description.trim() || undefined,
        })),
      }));

      this.$emit('send', {
        type: 'list',
        description: this.body.trim(),
        buttonText: this.buttonText.trim(),
        sections: sectionsPayload,
        footer: this.footer.trim() || undefined,
      });

      this.isSending = false;
      this.onClose();
    },
    resetForm() {
      this.body = '';
      this.buttonText = '';
      this.footer = '';
      this.sections = [
        {
          title: '',
          rows: [{ id: '1', title: '', description: '' }],
        },
      ];
      this.isSending = false;
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="flex flex-col p-6 max-h-[80vh] overflow-y-auto">
      <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
        {{ $t('CONVERSATION.DINASTIA_ACTIONS.LIST.TITLE') }}
      </h3>

      <!-- Body -->
      <div class="mb-4">
        <label class="block text-sm font-medium text-n-slate-11 mb-1">
          {{ $t('CONVERSATION.DINASTIA_ACTIONS.LIST.BODY') }} *
        </label>
        <textarea
          v-model="body"
          class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm text-n-slate-12 bg-white dark:bg-n-solid-2 focus:ring-2 focus:ring-woot-500 focus:border-woot-500 min-h-[80px]"
          :placeholder="
            $t('CONVERSATION.DINASTIA_ACTIONS.LIST.BODY_PLACEHOLDER')
          "
          maxlength="1024"
        />
        <span class="text-xs text-n-slate-9 mt-1">{{
          `${body.length}/1024`
        }}</span>
      </div>

      <!-- Button text -->
      <div class="mb-4">
        <label class="block text-sm font-medium text-n-slate-11 mb-1">
          {{ $t('CONVERSATION.DINASTIA_ACTIONS.LIST.BUTTON_TEXT') }} *
        </label>
        <input
          v-model="buttonText"
          type="text"
          class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm text-n-slate-12 bg-white dark:bg-n-solid-2 focus:ring-2 focus:ring-woot-500"
          :placeholder="
            $t('CONVERSATION.DINASTIA_ACTIONS.LIST.BUTTON_TEXT_PLACEHOLDER')
          "
          maxlength="20"
        />
        <span class="text-xs text-n-slate-9 mt-1">
          {{ `${buttonText.length}/20` }}
        </span>
      </div>

      <!-- Footer -->
      <div class="mb-4">
        <label class="block text-sm font-medium text-n-slate-11 mb-1">
          {{ $t('CONVERSATION.DINASTIA_ACTIONS.LIST.FOOTER') }}
        </label>
        <input
          v-model="footer"
          type="text"
          class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm text-n-slate-12 bg-white dark:bg-n-solid-2 focus:ring-2 focus:ring-woot-500"
          :placeholder="
            $t('CONVERSATION.DINASTIA_ACTIONS.LIST.FOOTER_PLACEHOLDER')
          "
          maxlength="60"
        />
      </div>

      <!-- Sections -->
      <div class="mb-4">
        <label class="block text-sm font-medium text-n-slate-11 mb-2">
          {{ $t('CONVERSATION.DINASTIA_ACTIONS.LIST.SECTIONS_LABEL') }}
        </label>

        <div
          v-for="(section, sIdx) in sections"
          :key="sIdx"
          class="mb-3 p-3 bg-n-alpha-1 rounded-lg border border-n-weak"
        >
          <div class="flex items-center gap-2 mb-2">
            <input
              v-model="section.title"
              type="text"
              class="flex-1 border border-n-weak rounded-lg px-3 py-1.5 text-sm text-n-slate-12 bg-white dark:bg-n-solid-2 focus:ring-2 focus:ring-woot-500"
              :placeholder="
                $t(
                  'CONVERSATION.DINASTIA_ACTIONS.LIST.SECTION_TITLE_PLACEHOLDER',
                  { n: sIdx + 1 }
                )
              "
              maxlength="24"
            />
            <woot-button
              v-if="sections.length > 1"
              variant="clear"
              size="tiny"
              color-scheme="alert"
              icon="dismiss"
              @click="removeSection(sIdx)"
            />
          </div>

          <!-- Rows -->
          <div
            v-for="(row, rIdx) in section.rows"
            :key="rIdx"
            class="flex items-start gap-2 mb-2 ml-4"
          >
            <div class="flex-1 space-y-1">
              <input
                v-model="row.title"
                type="text"
                class="w-full border border-n-weak rounded-lg px-3 py-1.5 text-sm text-n-slate-12 bg-white dark:bg-n-solid-2 focus:ring-2 focus:ring-woot-500"
                :placeholder="
                  $t(
                    'CONVERSATION.DINASTIA_ACTIONS.LIST.ROW_TITLE_PLACEHOLDER',
                    { n: rIdx + 1 }
                  )
                "
                maxlength="24"
              />
              <input
                v-model="row.description"
                type="text"
                class="w-full border border-n-weak rounded-lg px-3 py-1 text-xs text-n-slate-11 bg-white dark:bg-n-solid-2 focus:ring-2 focus:ring-woot-500"
                :placeholder="
                  $t(
                    'CONVERSATION.DINASTIA_ACTIONS.LIST.ROW_DESCRIPTION_PLACEHOLDER'
                  )
                "
                maxlength="72"
              />
            </div>
            <woot-button
              v-if="section.rows.length > 1"
              variant="clear"
              size="tiny"
              color-scheme="alert"
              icon="dismiss"
              @click="removeRow(sIdx, rIdx)"
            />
          </div>

          <woot-button
            v-if="section.rows.length < 10"
            variant="clear"
            size="small"
            icon="add"
            @click="addRow(sIdx)"
          >
            {{ $t('CONVERSATION.DINASTIA_ACTIONS.LIST.ADD_ROW') }}
          </woot-button>
        </div>

        <woot-button
          v-if="sections.length < 10"
          variant="clear"
          size="small"
          icon="add"
          @click="addSection"
        >
          {{ $t('CONVERSATION.DINASTIA_ACTIONS.LIST.ADD_SECTION') }}
        </woot-button>
      </div>

      <!-- Preview -->
      <div class="mb-4 p-3 bg-n-alpha-1 rounded-lg border border-n-weak">
        <p class="text-xs font-medium text-n-slate-9 mb-2">
          {{ $t('CONVERSATION.DINASTIA_ACTIONS.LIST.PREVIEW') }}
        </p>
        <div
          class="bg-white dark:bg-n-solid-2 rounded-lg p-3 shadow-sm border border-n-weak max-w-[280px]"
        >
          <p class="text-sm text-n-slate-12 mb-2 whitespace-pre-wrap">
            {{ body || '...' }}
          </p>
          <p v-if="footer" class="text-xs text-n-slate-9 mb-2">
            {{ footer }}
          </p>
          <div
            class="text-center py-1.5 text-sm text-n-blue-11 border border-n-blue-6 rounded bg-n-blue-2"
          >
            {{ buttonText || '...' }}
          </div>
          <div class="mt-2 text-xs text-n-slate-9">
            <div
              v-for="(section, sIdx) in validSections"
              :key="sIdx"
              class="mt-1"
            >
              <p
                v-if="section.title"
                class="font-medium text-n-slate-11 mb-0.5"
              >
                {{ section.title }}
              </p>
              <p
                v-for="(row, rIdx) in section.validRows"
                :key="rIdx"
                class="ml-2"
              >
                &bull; {{ row.title }}
                <span v-if="row.description" class="text-n-slate-8">
                  — {{ row.description }}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-2">
        <woot-button variant="clear" @click="onClose">
          {{ $t('CANCEL') }}
        </woot-button>
        <woot-button :disabled="!isValid" :is-loading="isSending" @click="send">
          {{ $t('CONVERSATION.DINASTIA_ACTIONS.LIST.SEND') }}
        </woot-button>
      </div>
    </div>
  </woot-modal>
</template>
